import * as React from 'react';
import './attackVisuals.css';

/* ------------------------------------------------------------------ */
/* Small inline SVG icons (stroke based, 24x24 viewBox)                */
/* ------------------------------------------------------------------ */

type IconProps = { children: React.ReactNode; size?: number; fill?: boolean };

function Icon({ children, size = 16, fill = false }: IconProps): JSX.Element {
  return (
    <svg
      className="av-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill ? 'currentColor' : 'none'}
      stroke={fill ? 'none' : 'currentColor'}
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const IcReply = (): JSX.Element => (
  <Icon>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10a6 6 0 0 1 6 6v3" />
  </Icon>
);
const IcReplyAll = (): JSX.Element => (
  <Icon>
    <path d="m8 14-5-5 5-5" />
    <path d="M13 14 8 9l5-5" />
    <path d="M8 9h6a6 6 0 0 1 6 6v3" />
  </Icon>
);
const IcForward = (): JSX.Element => (
  <Icon>
    <path d="m15 14 5-5-5-5" />
    <path d="M20 9H10a6 6 0 0 0-6 6v3" />
  </Icon>
);
const IcArchive = (): JSX.Element => (
  <Icon>
    <rect x="3" y="4" width="18" height="4" rx="1" />
    <path d="M5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
    <path d="M10 12h4" />
  </Icon>
);
const IcTrash = (): JSX.Element => (
  <Icon>
    <path d="M4 7h16" />
    <path d="M9 7V4h6v3" />
    <path d="M6 7l1 13h10l1-13" />
  </Icon>
);
const IcStar = (): JSX.Element => (
  <Icon>
    <path d="m12 3 2.7 5.6 6.1.8-4.5 4.2 1.1 6-5.4-3-5.4 3 1.1-6L3.2 9.4l6.1-.8z" />
  </Icon>
);
const IcDots = (): JSX.Element => (
  <Icon fill>
    <circle cx="5" cy="12" r="1.7" />
    <circle cx="12" cy="12" r="1.7" />
    <circle cx="19" cy="12" r="1.7" />
  </Icon>
);
const IcSearch = (): JSX.Element => (
  <Icon size={14}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.2-4.2" />
  </Icon>
);
const IcInbox = (): JSX.Element => (
  <Icon>
    <path d="M3 13 6 5h12l3 8" />
    <path d="M3 13v6h18v-6h-5l-1.5 2.5h-5L8 13z" />
  </Icon>
);
const IcSend = (): JSX.Element => (
  <Icon>
    <path d="M21 3 10 14" />
    <path d="M21 3 14.5 21l-4.5-7-7-4.5z" />
  </Icon>
);
const IcDraft = (): JSX.Element => (
  <Icon>
    <path d="M5 4h10l4 4v12H5z" />
    <path d="M15 4v4h4" />
    <path d="M8 13h8M8 17h5" />
  </Icon>
);
const IcSpam = (): JSX.Element => (
  <Icon>
    <path d="M12 3 3 8v5c0 4 3.6 7 9 8 5.4-1 9-4 9-8V8z" />
    <path d="M12 9v4M12 16.5v.1" />
  </Icon>
);
const IcFolder = (): JSX.Element => (
  <Icon>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </Icon>
);
const IcPaperclip = (): JSX.Element => (
  <Icon size={14}>
    <path d="m20 11-8.5 8.5a5 5 0 0 1-7-7L13 4a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L14 7" />
  </Icon>
);
const IcLock = (): JSX.Element => (
  <Icon size={13}>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </Icon>
);
const IcOnion = (): JSX.Element => (
  <svg className="av-icon" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v3" />
    <path d="M12 6c4 3 7 6 7 10a7 7 0 0 1-14 0c0-4 3-7 7-10z" />
    <path d="M12 6c-1.8 3-2.5 6-2.5 10M12 6c1.8 3 2.5 6 2.5 10" />
  </svg>
);
const IcCart = (): JSX.Element => (
  <Icon>
    <path d="M3 4h3l2.2 11h10.2L20 8H7" />
    <circle cx="9.5" cy="19" r="1.3" />
    <circle cx="17" cy="19" r="1.3" />
  </Icon>
);
const IcBtc = (): JSX.Element => (
  <svg className="av-icon" width={26} height={26} viewBox="0 0 32 32" aria-hidden="true">
    <circle cx="16" cy="16" r="15" fill="#f7931a" />
    <text x="16" y="22.5" textAnchor="middle" fontSize="19" fontWeight="700" fill="#fff" fontFamily="Arial, sans-serif">
      {'\u20BF'}
    </text>
  </svg>
);

const IcShield = (): JSX.Element => (
  <Icon size={14}>
    <path d="M12 3 4 6.5V12c0 4.5 3.2 7.7 8 9 4.8-1.3 8-4.5 8-9V6.5z" />
    <path d="m8.5 12 2.5 2.5 4.5-5" />
  </Icon>
);

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0] ?? '';
  const second = parts.length > 1 ? parts[1] ?? '' : '';
  return (first.charAt(0) + second.charAt(0)).toUpperCase();
}

function starFill(rating: string | undefined): number {
  if (!rating) return 5;
  const m = /(\d+(?:\.\d+)?)\s*%/.exec(rating);
  if (!m || m[1] === undefined) return 5;
  const pct = parseFloat(m[1]);
  if (!isFinite(pct)) return 5;
  return Math.max(0, Math.min(5, (pct / 100) * 5));
}

const FOLDERS: ReadonlyArray<{ name: string; icon: () => JSX.Element; count?: number }> = [
  { name: 'Inbox', icon: IcInbox, count: 3 },
  { name: 'Sent', icon: IcSend },
  { name: 'Drafts', icon: IcDraft },
  { name: 'Junk', icon: IcSpam },
  { name: 'Archive', icon: IcFolder },
];

/* ------------------------------------------------------------------ */
/* AttackEmail                                                         */
/* ------------------------------------------------------------------ */

export function AttackEmail(props: {
  from: string;
  fromAddr: string;
  to: string;
  subject: string;
  date?: string;
  body: React.ReactNode;
  note?: React.ReactNode;
}): JSX.Element {
  const { from, fromAddr, to, subject, date, body, note } = props;
  return (
    <div className="av-root av-mail">
      <div className="av-mail-client">
        <div className="av-mail-topbar">
          <span className="av-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span className="av-mail-app">Mail</span>
          <div className="av-mail-search">
            <IcSearch />
            <span>Search mail</span>
          </div>
        </div>
        <div className="av-mail-main">
          <nav className="av-mail-rail" aria-hidden="true">
            {FOLDERS.map((f) => {
              const Ico = f.icon;
              const active = f.name === 'Inbox';
              return (
                <div key={f.name} className={'av-mail-folder' + (active ? ' av-active' : '')}>
                  <Ico />
                  <span className="av-mail-folder-name">{f.name}</span>
                  {f.count !== undefined && <span className="av-mail-count">{f.count}</span>}
                </div>
              );
            })}
          </nav>
          <section className="av-mail-pane">
            <div className="av-mail-toolbar">
              <button type="button" className="av-tool" tabIndex={-1} aria-label="Reply">
                <IcReply />
                <span>Reply</span>
              </button>
              <button type="button" className="av-tool" tabIndex={-1} aria-label="Reply all">
                <IcReplyAll />
                <span>Reply all</span>
              </button>
              <button type="button" className="av-tool" tabIndex={-1} aria-label="Forward">
                <IcForward />
                <span>Forward</span>
              </button>
              <span className="av-tool-sep" />
              <button type="button" className="av-tool av-tool-icon" tabIndex={-1} aria-label="Archive">
                <IcArchive />
              </button>
              <button type="button" className="av-tool av-tool-icon" tabIndex={-1} aria-label="Delete">
                <IcTrash />
              </button>
              <span className="av-tool-grow" />
              <button type="button" className="av-tool av-tool-icon" tabIndex={-1} aria-label="More">
                <IcDots />
              </button>
            </div>
            <div className="av-mail-scroll">
              <div className="av-mail-head">
                <h2 className="av-mail-subject">{subject}</h2>
                <div className="av-mail-sender">
                  <div className="av-avatar" aria-hidden="true">
                    {initials(from)}
                  </div>
                  <div className="av-mail-who">
                    <div className="av-mail-line1">
                      <span className="av-mail-name">{from}</span>
                      <span className="av-mail-addr">&lt;{fromAddr}&gt;</span>
                    </div>
                    <div className="av-mail-line2">to {to}</div>
                  </div>
                  <div className="av-mail-meta">
                    {date && <span className="av-mail-date">{date}</span>}
                    <span className="av-mail-star">
                      <IcStar />
                    </span>
                  </div>
                </div>
              </div>
              <div className="av-mail-body">{body}</div>
              <div className="av-mail-foot">
                <IcPaperclip />
                <span>No attachments</span>
              </div>
            </div>
          </section>
        </div>
      </div>
      {note !== undefined && note !== null && <div className="av-note">{note}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* DarkMarket                                                          */
/* ------------------------------------------------------------------ */

const ONION_ADDR = 'http://skd7fq3x2m4ndzv6hlp5gwa7btec2ytj.onion/listing/48213';

export function DarkMarket(props: {
  vendor: string;
  title: string;
  bullets: string[];
  price: string;
  rating?: string;
  posted?: string;
}): JSX.Element {
  const { vendor, title, bullets, price, rating, posted } = props;
  const fill = starFill(rating);
  const stars: JSX.Element[] = [];
  for (let i = 0; i < 5; i += 1) {
    const w = Math.max(0, Math.min(1, fill - i));
    stars.push(
      <span className="av-star" key={i}>
        <span className="av-star-bg">{'\u2605'}</span>
        <span className="av-star-fg" style={{ width: Math.round(w * 100) + '%' }}>
          {'\u2605'}
        </span>
      </span>,
    );
  }
  return (
    <div className="av-root av-mk">
      <div className="av-br-chrome">
        <span className="av-dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <div className="av-br-tab">
          <IcOnion />
          <span>Listing #48213 - Dumps</span>
        </div>
      </div>
      <div className="av-br-bar">
        <div className="av-br-addr">
          <span className="av-br-warn" title="Not encrypted by HTTPS">
            <IcLock />
          </span>
          <span className="av-br-url">{ONION_ADDR}</span>
        </div>
        <span className="av-br-tor">
          <IcOnion />
          <span>Tor</span>
        </span>
      </div>
      <div className="av-mk-page">
        <header className="av-mk-header">
          <div className="av-mk-logo">
            <span className="av-mk-logo-mark">{'\u25C6'}</span>
            <span className="av-mk-logo-text">NIGHTSHADE MARKET</span>
          </div>
          <div className="av-mk-hdr-right">
            <span className="av-mk-chip">
              <span className="av-mk-chip-dot" />
              logged in as <b>{vendor}</b>
            </span>
            <span className="av-mk-cart">
              <IcCart />
              <span className="av-mk-cart-n">0</span>
            </span>
          </div>
        </header>
        <div className="av-mk-crumbs">
          <span>Home</span>
          <i>{'\u203A'}</i>
          <span>Fraud</span>
          <i>{'\u203A'}</i>
          <span>Dumps</span>
          <i>{'\u203A'}</i>
          <span className="av-cur">Payment cards</span>
        </div>
        {posted && (
          <div className="av-mk-posted">
            <span className="av-mk-posted-dot" />
            <b>Just posted</b>
            <span>{'\u2014 ' + posted}</span>
          </div>
        )}
        <div className="av-mk-listing">
          <div className="av-mk-main">
            <div className="av-mk-badges">
              <span className="av-badge av-badge-fresh">FRESH</span>
              <span className="av-badge av-badge-ver">
                <IcShield />
                verified vendor
              </span>
            </div>
            <h2 className="av-mk-title">{title}</h2>
            <div className="av-mk-vendor">
              <span>Sold by</span>
              <b className="av-mk-vname">{vendor}</b>
              <span className="av-mk-stars">{stars}</span>
              {rating && <span className="av-mk-rating">{rating}</span>}
            </div>
            <div className="av-mk-desc-h">Description</div>
            <ul className="av-mk-desc">
              {bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
            <div className="av-mk-facts">
              <div>
                <span>Stock</span>
                <b>1 lot</b>
              </div>
              <div>
                <span>Delivery</span>
                <b>Instant, encrypted file</b>
              </div>
              <div>
                <span>Payment</span>
                <b>Escrow (multisig)</b>
              </div>
            </div>
          </div>
          <aside className="av-mk-buy">
            <div className="av-mk-price-l">Price</div>
            <div className="av-mk-price">
              <IcBtc />
              <span>{price}</span>
            </div>
            <div className="av-mk-escrow">
              <IcShield />
              <span>Escrow protected. Funds release after finalize.</span>
            </div>
            <button type="button" className="av-mk-btn av-mk-btn-primary" tabIndex={-1}>
              Buy now
            </button>
            <button type="button" className="av-mk-btn" tabIndex={-1}>
              Add to cart
            </button>
            <div className="av-mk-small">Ships to: Worldwide (digital)</div>
          </aside>
        </div>
      </div>
    </div>
  );
}
